import { Fragment, type Ref } from 'react'
import { type AvatarSubject, Avatar } from '@/components/Avatar'
import { Icon } from '@/components/Icon'
import { Skeleton, useSkeletonListContext } from '@/components/Skeleton'
import styles from './JobEvent.module.css'
import { dateFormatters } from '@/utils/date-util'
import { type JobEventDescriptionPart } from '@/features/jobs/details/job-event-formatters'

export interface JobEventData {
  triggeredBy: AvatarSubject
  descriptionParts: JobEventDescriptionPart[]
  isTaskUpdate: boolean
  dateCreated: Date
  reason: string
}

export interface JobEventProps {
  event: JobEventData
  /**
   * A single line with the date at the end, for the refreshed activity list.
   */
  compact?: boolean
  testId?: string
}

const compactDateFormatter = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour12: true,
  hour: 'numeric',
  minute: '2-digit',
})

export function JobEvent({ event, compact, testId }: JobEventProps) {
  if (compact) {
    return (
      <div className={styles.compact} data-test={testId}>
        <div className={styles.compactLine}>
          <Avatar size={22} subject={event.triggeredBy} />
          <EventDescription event={event} />
          <span className={styles.compactDate}>
            {compactDateFormatter.format(event.dateCreated)}
          </span>
        </div>
        {event.reason && (
          <div className={styles.compactReason}>{event.reason}</div>
        )}
      </div>
    )
  }

  const isTaskUpdate = event.isTaskUpdate

  return (
    <div className={styles.root} data-test={testId}>
      <div className={styles.container}>
        <div className={styles.avatarContainer}>
          <Avatar size={32} subject={event.triggeredBy} />
        </div>
        <div className={styles.content}>
          <EventDescription event={event} />
          <div className={styles.info}>
            <div
              data-test={isTaskUpdate ? 'task-updates' : 'job-updates'}
              className={
                isTaskUpdate ? styles.iconTaskUpdate : styles.iconJobUpdate
              }
            >
              <Icon name={isTaskUpdate ? 'task-updates' : 'job-updates'} />
            </div>
            <div className={styles.date}>
              {dateFormatters.dateTimeDefault.format(event.dateCreated)}
            </div>
          </div>
        </div>
      </div>
      {event.reason && <div className={styles.reason}>{event.reason}</div>}
    </div>
  )
}

/**
 * A placeholder for a compact {@link JobEvent} still loading. Render it inside
 * a `SkeletonList` or `SkeletonListInfiniteScrollTrigger`.
 */
export function JobEventSkeleton() {
  const { varyPercent, ref } = useSkeletonListContext()
  return (
    <div
      ref={ref as Ref<HTMLDivElement> | undefined}
      className={styles.compact}
      aria-hidden
    >
      <div className={styles.compactLine}>
        <Skeleton block variant="circle" width={22} height={22} />
        <p className={styles.description}>
          <Skeleton width={varyPercent(55)} />
        </p>
        <span className={styles.compactDate}>
          <Skeleton width={84} />
        </span>
      </div>
    </div>
  )
}

interface EventDescriptionProps {
  event: JobEventData
}

function EventDescription({ event }: EventDescriptionProps) {
  return (
    <p className={styles.description}>
      {event.descriptionParts.map((part, index) =>
        part.isStrong ? (
          <strong key={index}>{part.text}</strong>
        ) : (
          <Fragment key={index}>{part.text}</Fragment>
        ),
      )}
    </p>
  )
}
